import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  UserPlus,
  Info,
  Share2,
  Hammer,
  XCircle,
  StopCircle,
  Shield,
  MessageSquare,
  UserCheck,
  Check,
  Trash2,
  X,
  ChevronDown,
  ArrowUpDown,
  Settings,
} from 'lucide-react'
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  bulkMarkRead,
  bulkDeleteNotifications,
  clearNotificationType,
  deleteNotification,
  type Notification,
} from '../lib/api'
import { formatRelativeTime } from '../lib/format'
import { formatNotificationMessage } from '../lib/notificationFormat'
import { getNotificationLink } from '../lib/notificationLinks'
import { useUser } from '../hooks/useUser'
import { useNotificationCount } from '../hooks/useNotificationCount'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useAutoMarkRead } from '../hooks/useAutoMarkRead'
import { NotificationPreferencesModal } from '../components/NotificationPreferencesModal'

interface TypeConfig {
  icon: React.ElementType
  color: string
  isAdmin?: boolean
}

const typeConfig: Record<string, TypeConfig> = {
  app_shared: { icon: Share2, color: 'text-blue-500' },
  access_request: { icon: UserPlus, color: 'text-purple-500' },
  access_approved: { icon: UserCheck, color: 'text-green-500' },
  access_denied: { icon: XCircle, color: 'text-red-500' },
  production_build_triggered: { icon: Hammer, color: 'text-amber-500' },
  production_build_success: { icon: CheckCircle2, color: 'text-green-500' },
  production_build_failed: { icon: XCircle, color: 'text-red-500' },
  app_error: { icon: AlertTriangle, color: 'text-red-500' },
  app_auto_stopped: { icon: StopCircle, color: 'text-muted-foreground' },
  feedback_comment: { icon: MessageSquare, color: 'text-blue-500' },
  feedback_status: { icon: CheckCircle2, color: 'text-green-500' },
  new_user_registered: { icon: UserPlus, color: 'text-blue-500', isAdmin: true },
  secret_scan_finding: { icon: Shield, color: 'text-red-500', isAdmin: true },
  new_feedback: { icon: MessageSquare, color: 'text-purple-500', isAdmin: true },
}

function getConfig(type: string): TypeConfig {
  return typeConfig[type] || { icon: Info, color: 'text-muted-foreground' }
}

// Type groups for the filter dropdown
const appTypes = [
  'app_shared', 'access_request', 'access_approved', 'access_denied',
  'production_build_triggered', 'production_build_success', 'production_build_failed',
  'app_error', 'app_auto_stopped', 'feedback_comment', 'feedback_status',
]
const adminTypes = [
  'new_user_registered', 'secret_scan_finding', 'new_feedback',
]

type SortOption = 'newest' | 'priority' | 'oldest'
type ReadFilter = 'all' | 'unread'
type TypeFilter = 'all' | 'apps' | 'admin' | string // string for individual types

// Catalogs are imported from ../lib/notificationCatalog at the top of the file.

const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 }

function sortNotifications(list: Notification[], sort: SortOption): Notification[] {
  return [...list].sort((a, b) => {
    if (sort === 'priority') {
      const pa = priorityOrder[a.priority || 'low'] ?? 2
      const pb = priorityOrder[b.priority || 'low'] ?? 2
      if (pa !== pb) return pa - pb
      // Secondary sort by newest
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    }
    if (sort === 'oldest') {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    }
    // newest (default)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
}

function getPriorityIndicator(priority?: string) {
  if (priority === 'high') return 'bg-red-500'
  if (priority === 'medium') return 'bg-orange-400'
  return null
}

export default function Notifications() {
  const navigate = useNavigate()
  const user = useUser()
  const { adjustCount, refreshCount } = useNotificationCount()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [readFilter, setReadFilter] = useState<ReadFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [sort, setSort] = useState<SortOption>('newest')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [selectionMode, setSelectionMode] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)
  const [scopeFilter, setScopeFilter] = useState<'all' | 'my-apps' | 'platform'>('all')
  const [showPrefs, setShowPrefs] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const PAGE_SIZE = 50

  const handleAutoMarkedRead = useCallback((ids: number[]) => {
    const idSet = new Set(ids)
    setNotifications(prev => prev.map(n => idSet.has(n.id) ? { ...n, is_read: true } : n))
    adjustCount(-ids.length)
  }, [adjustCount])

  const { observe } = useAutoMarkRead(handleAutoMarkedRead)

  const load = useCallback(async () => {
    try {
      const data = await listNotifications({ limit: PAGE_SIZE })
      setNotifications(data)
      setHasMore(data.length === PAGE_SIZE)
    } catch {
      setNotifications([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const data = await listNotifications({ limit: PAGE_SIZE, offset: notifications.length })
      setNotifications(prev => [...prev, ...data])
      setHasMore(data.length === PAGE_SIZE)
    } catch {
      setHasMore(false)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, notifications.length])

  useEffect(() => { load() }, [load])

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore() },
      { threshold: 0.1 },
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, loadMore])


  // Auto-refresh notification list every 15s while page is open
  useAutoRefresh(load, { baseInterval: 15000, maxInterval: 60000 })

  // Close dropdowns on outside click
  useEffect(() => {
    if (!sortOpen && !typeDropdownOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-dropdown]')) {
        setSortOpen(false)
        setTypeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [sortOpen, typeDropdownOpen])

  async function handleMarkRead(id: number) {
    try {
      await markNotificationRead(id)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
      adjustCount(-1)
    } catch { /* */ }
  }

  async function handleMarkAllRead() {
    try {
      await markAllNotificationsRead()
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      refreshCount()
    } catch { /* */ }
  }

  async function handleDelete(id: number) {
    try {
      const wasUnread = notifications.find(n => n.id === id && !n.is_read)
      await deleteNotification(id)
      setNotifications(prev => prev.filter(n => n.id !== id))
      setSelected(prev => { const next = new Set(prev); next.delete(id); return next })
      if (wasUnread) adjustCount(-1)
    } catch { /* */ }
  }

  async function handleBulkMarkRead() {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    try {
      const unreadCount = ids.filter(id => notifications.find(n => n.id === id && !n.is_read)).length
      await bulkMarkRead(ids)
      setNotifications(prev => prev.map(n => selected.has(n.id) ? { ...n, is_read: true } : n))
      setSelected(new Set())
      setSelectionMode(false)
      adjustCount(-unreadCount)
    } catch { /* */ }
  }

  async function handleBulkDelete() {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    try {
      const unreadCount = ids.filter(id => notifications.find(n => n.id === id && !n.is_read)).length
      await bulkDeleteNotifications(ids)
      setNotifications(prev => prev.filter(n => !selected.has(n.id)))
      setSelected(new Set())
      setSelectionMode(false)
      adjustCount(-unreadCount)
    } catch { /* */ }
  }

  async function handleClearType(type: string) {
    try {
      const unreadOfType = notifications.filter(n => n.type === type && !n.is_read).length
      await clearNotificationType(type)
      setNotifications(prev => prev.filter(n => n.type !== type))
      setSelected(prev => {
        const next = new Set(prev)
        notifications.filter(n => n.type === type).forEach(n => next.delete(n.id))
        return next
      })
      adjustCount(-unreadOfType)
    } catch { /* */ }
  }

  function handleClick(notif: Notification) {
    if (!notif.is_read) handleMarkRead(notif.id)
    const link = getNotificationLink(notif)
    if (link) navigate(link)
  }

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Apply filters
  const filtered = useMemo(() => {
    let list = notifications

    // Read filter
    if (readFilter === 'unread') {
      list = list.filter(n => !n.is_read)
    }

    // Type filter
    if (typeFilter === 'apps') {
      list = list.filter(n => appTypes.includes(n.type))
    } else if (typeFilter === 'admin') {
      list = list.filter(n => adminTypes.includes(n.type))
    } else if (typeFilter !== 'all') {
      list = list.filter(n => n.type === typeFilter)
    }

    // Scope filter (admin only)
    if (scopeFilter === 'my-apps') {
      list = list.filter(n => !adminTypes.includes(n.type))
    } else if (scopeFilter === 'platform') {
      list = list.filter(n => adminTypes.includes(n.type))
    }

    // Sort
    return sortNotifications(list, sort)
  }, [notifications, readFilter, typeFilter, scopeFilter, sort])

  const unreadCount = notifications.filter(n => !n.is_read).length

  // Determine the active type filter label for display
  const activeTypeLabel = useMemo(() => {
    if (typeFilter === 'all') return null
    if (typeFilter === 'apps') return 'Apps'
    if (typeFilter === 'admin') return 'Admin'
    // Individual type: humanize
    return typeFilter.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }, [typeFilter])

  // Collect unique types present in current notifications for the type dropdown
  const presentTypes = useMemo(() => {
    const types = new Set(notifications.map(n => n.type))
    return Array.from(types)
  }, [notifications])

  const hasAppTypes = presentTypes.some(t => appTypes.includes(t))
  const hasAdminTypes = presentTypes.some(t => adminTypes.includes(t))

  const allFilteredIds = filtered.map(n => n.id)
  const allSelected = filtered.length > 0 && filtered.every(n => selected.has(n.id))

  function toggleSelectAll() {
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev)
        allFilteredIds.forEach(id => next.delete(id))
        return next
      })
    } else {
      setSelected(prev => {
        const next = new Set(prev)
        allFilteredIds.forEach(id => next.add(id))
        return next
      })
    }
  }

  const sortLabels: Record<SortOption, string> = {
    newest: 'Newest',
    priority: 'Priority',
    oldest: 'Oldest',
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header — wraps on mobile so the action row drops below the title */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Select / Cancel button */}
          <button
            onClick={() => {
              if (selectionMode) {
                setSelectionMode(false)
                setSelected(new Set())
              } else {
                setSelectionMode(true)
              }
            }}
            className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors ${
              selectionMode
                ? 'border-primary/50 bg-primary/5 text-primary hover:bg-primary/10'
                : 'border-border hover:bg-secondary'
            }`}
          >
            {selectionMode ? (
              <>
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Cancel</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span className="hidden sm:inline">Select</span>
              </>
            )}
          </button>
          {/* Mark all read */}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
            >
              <Check className="w-4 h-4" />
              <span className="hidden sm:inline">Mark all read</span>
            </button>
          )}
          {/* Preferences */}
          <button
            onClick={() => setShowPrefs(true)}
            className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Preferences</span>
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Read filter pills */}
        <div className="flex items-center border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => setReadFilter('all')}
            className={`px-3 py-1.5 text-sm transition-colors ${
              readFilter === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setReadFilter('unread')}
            className={`px-3 py-1.5 text-sm transition-colors ${
              readFilter === 'unread' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
            }`}
          >
            Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
          </button>
        </div>

        {/* Scope filter pills (admin only) */}
        {user.isAdmin && (
          <div className="flex items-center border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setScopeFilter('all')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                scopeFilter === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setScopeFilter('my-apps')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                scopeFilter === 'my-apps' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
              }`}
            >
              My Apps
            </button>
            <button
              onClick={() => setScopeFilter('platform')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                scopeFilter === 'platform' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
              }`}
            >
              Platform
            </button>
          </div>
        )}

        {/* Type filter dropdown */}
        <div className="relative" data-dropdown>
          <button
            onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setSortOpen(false) }}
            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm transition-colors ${
              typeFilter !== 'all'
                ? 'border-primary/50 bg-primary/5 text-primary'
                : 'border-border hover:bg-secondary'
            }`}
          >
            {activeTypeLabel || 'Type'}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {typeDropdownOpen && (
            <div className="absolute left-0 top-full mt-1 w-56 bg-card border border-border rounded-lg shadow-lg z-20 py-1">
              <button
                onClick={() => { setTypeFilter('all'); setTypeDropdownOpen(false) }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors ${
                  typeFilter === 'all' ? 'text-primary font-medium' : 'text-foreground'
                }`}
              >
                All types
              </button>
              <div className="border-t border-border my-1" />
              {hasAppTypes && (
                <button
                  onClick={() => { setTypeFilter('apps'); setTypeDropdownOpen(false) }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors ${
                    typeFilter === 'apps' ? 'text-primary font-medium' : 'text-foreground'
                  }`}
                >
                  Apps
                </button>
              )}
              {user.isAdmin && hasAdminTypes && (
                <button
                  onClick={() => { setTypeFilter('admin'); setTypeDropdownOpen(false) }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors ${
                    typeFilter === 'admin' ? 'text-primary font-medium' : 'text-foreground'
                  }`}
                >
                  Admin
                </button>
              )}
              <div className="border-t border-border my-1" />
              {presentTypes.map(t => {
                const cfg = getConfig(t)
                if (cfg.isAdmin && !user.isAdmin) return null
                const Icon = cfg.icon
                const label = t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                return (
                  <button
                    key={t}
                    onClick={() => { setTypeFilter(t); setTypeDropdownOpen(false) }}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors flex items-center gap-2 ${
                      typeFilter === t ? 'text-primary font-medium' : 'text-foreground'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                    {label}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Sort dropdown */}
        <div className="relative" data-dropdown>
          <button
            onClick={() => { setSortOpen(!sortOpen); setTypeDropdownOpen(false) }}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-lg text-sm hover:bg-secondary transition-colors"
          >
            <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
            {sortLabels[sort]}
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          {sortOpen && (
            <div className="absolute left-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-20 py-1">
              {(Object.keys(sortLabels) as SortOption[]).map(opt => (
                <button
                  key={opt}
                  onClick={() => { setSort(opt); setSortOpen(false) }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors ${
                    sort === opt ? 'text-primary font-medium' : 'text-foreground'
                  }`}
                >
                  {sortLabels[opt]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active type filter: clear button */}
        {typeFilter !== 'all' && (
          <button
            onClick={() => setTypeFilter('all')}
            className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-3 h-3" />
            Clear filter
          </button>
        )}

        {/* Clear all of type button (when filtering by specific type) */}
        {typeFilter !== 'all' && typeFilter !== 'apps' && typeFilter !== 'admin' && filtered.length > 0 && (
          <button
            onClick={() => handleClearType(typeFilter)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 hover:text-red-600 hover:bg-red-500/5 rounded-lg transition-colors ml-auto"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Clear all {activeTypeLabel}
          </button>
        )}
      </div>

      {/* Bulk actions bar */}
      {selectionMode && selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleBulkMarkRead}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-secondary transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              Mark read
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Notifications list */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Bell className="w-12 h-12 text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-semibold mb-1">
            {readFilter === 'unread' ? 'No unread notifications' : typeFilter !== 'all' ? 'No notifications of this type' : 'No notifications yet'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {readFilter === 'unread' ? 'You\'re all caught up!' : 'Notifications will appear here when something happens.'}
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden bg-card">
          {/* Select all header */}
          <div className="flex items-center gap-3 px-4 py-2 bg-secondary/30 border-b border-border">
            {selectionMode && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
                />
                <span className="text-xs text-muted-foreground">Select all</span>
              </label>
            )}
            <span className={`text-xs text-muted-foreground ${selectionMode ? 'ml-auto' : ''}`}>
              {filtered.length} notification{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Notification rows */}
          <div className="divide-y divide-border">
            {filtered.map(notif => {
              const cfg = getConfig(notif.type)
              const Icon = cfg.icon
              const link = getNotificationLink(notif)
              const priorityDot = getPriorityIndicator(notif.priority)
              const isSelected = selected.has(notif.id)
              return (
                <div
                  key={notif.id}
                  ref={!notif.is_read ? (el) => observe(notif.id, el) : undefined}
                  className={`group flex items-center gap-3 px-4 py-3 transition-colors ${
                    link ? 'cursor-pointer' : ''
                  } ${
                    isSelected
                      ? 'bg-primary/[0.05]'
                      : !notif.is_read
                        ? 'bg-primary/[0.03] hover:bg-primary/[0.06]'
                        : 'hover:bg-secondary/30'
                  }`}
                >
                  {/* Checkbox (only in selection mode) */}
                  {selectionMode && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(notif.id) }}
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0 w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
                    />
                  )}

                  {/* Priority dot + unread indicator */}
                  <div className="shrink-0 w-2 flex items-center justify-center">
                    {priorityDot ? (
                      <div className={`w-2 h-2 rounded-full ${priorityDot}`} title={`${notif.priority} priority`} />
                    ) : !notif.is_read ? (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    ) : null}
                  </div>

                  {/* Icon */}
                  <div className={`shrink-0 ${cfg.color}`} onClick={() => handleClick(notif)}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" onClick={() => handleClick(notif)}>
                    <p className={`text-sm ${!notif.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {formatNotificationMessage(notif)}
                      {cfg.isAdmin && (
                        <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-semibold bg-amber-500/20 text-amber-600 dark:text-amber-400 rounded align-middle">
                          Admin
                        </span>
                      )}
                      {notif.group_count && notif.group_count > 1 && (
                        <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-semibold bg-secondary text-muted-foreground rounded-full align-middle">
                          x{notif.group_count}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Time */}
                  <span className="shrink-0 text-xs text-muted-foreground/70 tabular-nums">
                    {formatRelativeTime(notif.created_at)}
                  </span>

                  {/* Delete button (visible on hover) */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(notif.id) }}
                    className="shrink-0 p-1 rounded text-muted-foreground/50 hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                    title="Delete notification"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )
            })}

            {/* Infinite scroll sentinel */}
            {hasMore && (
              <div ref={sentinelRef} className="flex items-center justify-center py-4">
                {loadingMore ? (
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <span className="text-xs text-muted-foreground">Scroll for more</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <NotificationPreferencesModal open={showPrefs} onClose={() => setShowPrefs(false)} />
    </div>
  )
}
